import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback, useEffect, useRef } from 'react';

import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { sessionStorage } from '#src/lib/storage/sessionStorage.ts';

import type {
  SetTwaDeviceTokenMutation,
  SetTwaDeviceTokenMutationVariables,
} from './__generated__/useTwaDeviceToken.graphql.ts';
import { useMutation } from './graphql/useMutation.ts';
import { useIsMobileApp } from './useIsMobileApp.ts';

// The TWA wraps the website inside the Android app, so the backend delivers to
// its FCM tokens through the existing Android device platform.
const TWA_DEVICE_PLATFORM = 'com.sorare.app.android';

const SET_TWA_DEVICE_TOKEN_MUTATION = gql`
  mutation SetTwaDeviceTokenMutation($input: setDeviceTokenInput!) {
    setDeviceToken(input: $input) {
      errors {
        message
      }
    }
  }
` as TypedDocumentNode<
  SetTwaDeviceTokenMutation,
  SetTwaDeviceTokenMutationVariables
>;

// The Android TWA shell cannot call the API itself: it hands the device's FCM
// registration token to the web through the `fcmToken` query param (persisted
// to sessionStorage on boot), and the web registers it against the session.
const useTwaDeviceToken = () => {
  const { isTwa } = useIsMobileApp();
  return isTwa ? sessionStorage.getItem('fcmToken') : null;
};

// Registers the TWA's FCM token for the signed-in user, once per user and
// token, and again after a sign-out since the logout revokes it. The backend is
// idempotent, so a repeat after a reload is harmless.
export const useRegisterTwaDeviceToken = () => {
  const deviceToken = useTwaDeviceToken();
  const { currentUser } = useCurrentUserContext() || {};
  const userSlug = currentUser?.slug;
  const [setDeviceToken] = useMutation(SET_TWA_DEVICE_TOKEN_MUTATION, {
    sendErrors: true,
  });
  const registered = useRef<string | null>(null);

  useEffect(() => {
    if (!deviceToken || !userSlug) {
      registered.current = null;
      return;
    }
    const key = `${userSlug}:${deviceToken}`;
    if (registered.current === key) {
      return;
    }
    registered.current = key;

    setDeviceToken({
      variables: {
        input: { deviceToken, devicePlatform: TWA_DEVICE_PLATFORM },
      },
    }).then(({ success }) => {
      if (!success && registered.current === key) {
        // let a later user or token change retry
        registered.current = null;
      }
    });
  }, [deviceToken, userSlug, setDeviceToken]);
};

// Disables the TWA's FCM token for the signed-in user. Runs on logout, while
// the session cookie is still valid, so the next account on this device does
// not receive the previous one's notifications.
export const useRevokeTwaDeviceToken = () => {
  const deviceToken = useTwaDeviceToken();
  const { currentUser } = useCurrentUserContext() || {};
  const isLoggedIn = !!currentUser;
  const [setDeviceToken] = useMutation(SET_TWA_DEVICE_TOKEN_MUTATION, {
    sendErrors: true,
  });

  return useCallback(async () => {
    if (!deviceToken || !isLoggedIn) {
      return true;
    }
    const { success } = await setDeviceToken({
      variables: {
        input: {
          deviceToken,
          devicePlatform: TWA_DEVICE_PLATFORM,
          disable: true,
        },
      },
    });
    return success;
  }, [deviceToken, isLoggedIn, setDeviceToken]);
};
